import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent, DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useStoreState } from 'pullstate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import DataGridTooltips from '../../components/DataGridTooltips.jsx';
import { AppStore } from '../../stores/AppStore.js';
import { deleteGoalAPI } from '../../utils/apiFunctions';
import { deleteLocalArrayObject } from '../../utils/localStorageFunctions.js';
import AdditionalInformation from '../additionalInfo/AdditionalInformation.jsx';
import FirstGoalPDF from './FirstGoal.jsx';
import NotifyCoachReadyForApproval from './NotifyCoachReadyForApproval.jsx';
import NotifyPlayerNeedsRevision from './NotifyPlayerNeedsRevision.jsx';
import getDataGridStyles from '../../components/styles/dataGridStyles.js';

const Goals = (props) => {
    const { playerId, peopleBuilder, pbClosed } = props
    const { goals, checkIns, loading } = useStoreState(AppStore)
    const openAlert = useStoreState(AppStore, s => s.snackbar.openAlert);
    const [userGoals, setUserGoals] = useState([{
        goal_name: '',
        goal_type: '',
        target_date: '',
        status: '',
        last_check_in: '',
        check_in_status: '',
        id: 0
    }])
    const [openFirstGoalPDF, setOpenFirstGoalPDF] = useState(false)
    const navigate = useNavigate();
    const [openDelete, setOpenDelete] = useState(false)
    const [goalSelectedToDelete, setGoalSelectedToDelete] = useState({ goal_name: '', id: '' })
    const [userLogged] = useState(useStoreState(AppStore, s => s.user))
    const [isCoach] = useState(userLogged.full_name === peopleBuilder.player.coach)
    const [isPlayer] = useState(userLogged.id === peopleBuilder.player.id)
    const [isAdmin] = useState(userLogged.is_lifecycle_admin)
    const [loadingGoals, setLoadingGoals] = useState(false)
    const [userGoalsStatusActive, setUserGoalsStatusActive] = useState(false)
    const [showButtonNotifyCoach, setShowButtonNotifyCoach] = useState(false)
    const [goalNeedsRevision, setgoalNeedsRevision] = useState(false)
    const [pagination, setPagination] = useState({ page: 0, pageSize: 6 })

    const columns = useMemo(() => [
        {
            field: 'goal_name',
            headerName: 'Goal Name',
            headerClassName: 'header-class',
            width: 300,
            renderCell: (params) => {
                return (
                    <Stack direction='row' gap={1} alignItems='center' sx={{ width: '100%' }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                            {params.row.goal_name}
                        </div>
                        {params.row.inheritance && (
                            <Tooltip title='Inherited from last year'>
                                <Chip label='Inherited' color='primary' size='small' />
                            </Tooltip>
                        )}
                    </Stack>
                )
            }
        },
        {
            field: 'goal_type',
            headerName: 'Goal Type',
            headerClassName: 'header-class',
            width: 350,
        },
        {
            field: 'target_date',
            headerName: 'Target Date',
            headerClassName: 'header-class',
            width: 300,
            flex: 1
        },
        {
            field: 'status',
            headerName: 'Status',
            headerClassName: 'header-class',
            width: 150,
        },
        {
            field: 'last_check_in',
            headerName: 'Last Check-In',
            headerClassName: 'header-class',
            width: 300,
            flex: 1
        },
        {
            field: 'check_in_status',
            headerName: 'Check-In Status',
            headerClassName: 'header-class',
            width: 300,
            flex: 1
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerClassName: 'header-class',
            width: 300,
            flex: 1,
            renderCell: (params) => {
                return (
                    params.row.status === 'Active' || !params.row.pb_closed ?
                        (
                            <IconButton>
                                <VisibilityIcon />
                            </IconButton>
                        ) : (
                            <Stack direction='row' spacing={1}>
                                <IconButton onClick={(event) =>  {
                                    event.stopPropagation()
                                    handleRowClick(params.id, 'actions')
                                    }}
                                >
                                    <Tooltip title='Edit Goal'>
                                        <EditIcon />
                                    </Tooltip >
                                </IconButton >
                                <IconButton>
                                    <Tooltip title='Delete Goal'>
                                        <DeleteIcon
                                            className="delete-icon"
                                            onClick={() => {

                                                if (!isPlayer && !isCoach) {
                                                    openAlert(`You can only view your team's goals.`, 'info')
                                                    return
                                                }

                                                setOpenDelete(true)
                                                setGoalSelectedToDelete({ goal_name: params.row.goal_name, id: params.row.id })
                                            }} />
                                    </Tooltip>
                                </IconButton>
                            </Stack >
                        )
                )
            },
        },
    ], [pbClosed, isCoach, isPlayer])

    const handleClose = () => {
        setOpenFirstGoalPDF(false)
    }

    const handleRowClick = useCallback( async (goalId, from = 'row') => {
          const goalSelected = goals.find(goal => goal.id === goalId)
      
          if (!isCoach && !isPlayer && !isAdmin) {
            openAlert(`You can only view your team's goals.`, 'error')
            return
          }
      
          navigate(
            `/goal-form/people-builder/${goalSelected?.people_builder_id}/goal/${goalId}`,
            { state: { from } }
          )
        }, [goals])
      

    const openGoalForm = () => {
        const filterGoalsByUser = goals.filter(goal => goal?.people_builder?.player_id === playerId && !goal.goal_status.includes('archived') && peopleBuilder.id === goal.people_builder_id)

        if (!filterGoalsByUser.length) {
            setOpenFirstGoalPDF(true)
        } else {
            navigate(`/goal-form/people-builder/${peopleBuilder?.id}`)
        }

    }

    const findMostRecentCheckin = (checkins) => {
        if (!checkins?.length) return null;

        const checkInGoalsPlayer = checkIns.filter(checkIn => checkIn.check_in_goals.some(checkInGoal => checkInGoal.goal.people_builder_id === peopleBuilder.id))

        if (!checkInGoalsPlayer?.length) return null;

        return checkInGoalsPlayer?.reduce((mostRecent, current) => {
            return new Date(current.check_in_date) > new Date(mostRecent.check_in_date) ? current : mostRecent;
        });
    };

    const getUserGoals = useCallback(async () => {
        const userGoalsFiltered = goals?.filter(goal => goal?.people_builder?.player_id === playerId && !goal.goal_status.includes('archived') && peopleBuilder.id === goal.people_builder_id)
        const userCheckins = checkIns?.filter(checkin => checkin.player_id === playerId)
        const mostRecentCheckin = findMostRecentCheckin(userCheckins);

        const goalsFixed = userGoalsFiltered?.map((goal) => {

            if (!goal.goal_status === 'archived') return null

            return {
                goal_name: goal.goal_name,
                goal_type: goal.goal_term,
                target_date: goal.target_date ? new Date(goal.target_date)?.toISOString().split('T')[0] : '',
                status: goal.goal_status,
                // last_check_in: goal.last_check_in?.split('T')[0],
                // check_in_status: goal.checkin_status,
                last_check_in: mostRecentCheckin?.check_in_date?.split('T')[0] || '',
                check_in_status: mostRecentCheckin?.check_in_goals?.find(g => g.goal_id === goal.id)?.check_in_goal_status ?? '',
                id: goal.id,
                inheritance: goal.inheritance,
                pb_closed: pbClosed
            }
        })

        const statusUserGoals = userGoalsFiltered?.map(goal => goal.goal_status)
        const atLeastOneApproval = statusUserGoals?.filter(status => status === 'Ready for Approval').length >= 1
        const threeOrMoreGoals = statusUserGoals?.length >= 3
        const zeroDraftGoals = statusUserGoals?.filter(status => status === 'Draft Goal').length === 0
        const zeroNeedsRevisionGoals = statusUserGoals?.filter(status => status === 'Needs Revision').length === 0
        setShowButtonNotifyCoach(atLeastOneApproval && threeOrMoreGoals && zeroDraftGoals && zeroNeedsRevisionGoals)
        const statusActive = statusUserGoals?.length ? statusUserGoals.every(status => status === 'Active') : false
        setgoalNeedsRevision(statusUserGoals?.filter(status => status === 'Needs Revision').length > 0)
        setUserGoalsStatusActive(statusActive)
        
        if (goalsFixed) {
            goalsFixed.sort((a, b) => {
                return a.goal_name.localeCompare(b.goal_name)
            })
            setUserGoals(goalsFixed)
        }
        // setLoadingGoals(false)
    }, [goals, checkIns])

    const deleteGoal = async () => {
        AppStore.update(s => { s.loading = true })
        setOpenDelete(false)
        const deletedGoal = await deleteGoalAPI(goalSelectedToDelete.id)
        deleteLocalArrayObject('goals', goalSelectedToDelete.id)
        AppStore.update(s => { s.loading = false })
        openAlert(deletedGoal?.message)
    }

    const getRowClassName = (params) => {

        if (params.row.status === 'Draft Goal' && isCoach) {
            return 'disabled-row';
        } else if (!isCoach && !isPlayer && !isAdmin) {
            return 'disabled-row';
        }
        return '';
    };

    const openGoalFormAfterModal = () => {
        navigate(`/goal-form/people-builder/${peopleBuilder.id}`)
    }

    useEffect(() => {
        getUserGoals()
    }, [goals, checkIns])

    // useEffect(() => {
    //     setTimeout(() => {
    //         setLoadingGoals(false)
    //     }, 500);
    // }, [])

    return (
        <React.Fragment>
            <Paper>
                <BackdropLoader openBackdrop={loading} />
                {userGoalsStatusActive && (
                    <AdditionalInformation peopleBuilder={peopleBuilder} />
                )}
                <DataGrid
                    rows={userGoals}
                    columns={columns}
                    disableColumnResize={true}
                    rowSelection={false}
                    getRowClassName={getRowClassName}
                    loading={loadingGoals}
                    localeText={{ noRowsLabel: "No goals" }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'skeleton',
                            noRowsVariant: 'skeleton'
                        }
                    }}
                    onRowClick={(params, event) => {

                        if (!event.target.closest('.delete-icon')) {
                            handleRowClick(params.id)
                        }
                    }}
                    paginationModel={pagination}
                    pageSizeOptions={[5, 10]}
                    onPaginationModelChange={(params) => setPagination((prev) => ({ ...prev, pageSize: params.pageSize, page: params.page }))}
                    sx={getDataGridStyles}
                />

            </Paper>
            {
                pbClosed ? (
                    (isCoach || isPlayer) || isAdmin ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignContent: 'center', mt: 2, position: 'relative' }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                                <Tooltip title='Add New Goal'>
                                    <IconButton onClick={openGoalForm}>
                                        <AddCircleOutlineIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {(showButtonNotifyCoach && isPlayer) ? (
                                <Box sx={{ position: 'absolute', right: 0 }}>
                                    <NotifyCoachReadyForApproval peopleBuilder={peopleBuilder} />
                                </Box>
                            ) : (
                                goalNeedsRevision && isCoach && (
                                    <Box sx={{ position: 'absolute', right: 0 }}>
                                        <NotifyPlayerNeedsRevision peopleBuilder={peopleBuilder} />
                                    </Box>
                                )
                            )}
                        </Box>
                    ) : (
                        <Stack>
                            <Tooltip title={`You can only view your team's goals.`}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <IconButton disabled>
                                        <AddCircleOutlineIcon />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </Stack>
                    )

                ) : (
                    <Stack>
                        <Tooltip title={`People Builder is closed`}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <IconButton disabled>
                                    <AddCircleOutlineIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </Stack>
                )

            }
            <Dialog
                open={openFirstGoalPDF}
                onClose={handleClose}
                fullWidth
                maxWidth='xl'
                aria-labelledby="alert-dialog-first-goal"
                aria-describedby="alert-dialog-first-goal"
            >
                <DialogContent>
                    <FirstGoalPDF peopleBuilder={peopleBuilder} />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={openGoalFormAfterModal}
                        sx={{ m: 'auto' }}
                        variant='contained'
                    >
                        start your people builder
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDelete}
                // onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"WARNING!"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        You are about to delete {goalSelectedToDelete.goal_name}.
                        Are you sure you want to do this?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
                    <Button onClick={deleteGoal}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment >
    )
}

export default Goals